// Paging math for list-valued node IO. The run-IO endpoint caps every list at
// MAX_LIST_ITEMS_PER_OUTPUT items per response (spotflow/flow/stream.py) and
// serves the rest through `list_offset`, so the pager walks a long output one
// window at a time instead of stopping at the first 100 items.
export const IO_LIST_WINDOW = 100;

export function ioListWindowOffset(
  page: number,
  windowSize: number = IO_LIST_WINDOW
): number {
  if (page <= 1) return 0;
  return Math.floor((page - 1) / windowSize) * windowSize;
}

// Where `page` sits inside the window in hand. Out of range while a window
// fetch is still in flight — callers render that as "no value yet", not as
// item 0 of the wrong window.
export function ioListItemIndex(page: number, listOffset: number): number {
  return page - 1 - listOffset;
}

// Full list length. `total_size` is written with the whole list at write time,
// so it survives the endpoint's windowing; the received count is only a
// fallback for legacy payloads that never carried it.
export function ioListTotalItems(io: {
  value: unknown;
  total_size?: number;
  list_offset?: number;
}): number {
  if (!Array.isArray(io.value)) return 0;
  if (io.total_size && io.total_size > 0) return io.total_size;
  return (io.list_offset ?? 0) + io.value.length;
}

// Whether the pager should walk this container as a list. An empty window still
// counts when it is a *later* window: inputs and outputs share one cursor, so
// asking for offset 100 on behalf of a 340-item output returns nothing for a
// 5-item input. Reading that as "not a list" strands the cursor — the
// tab-switch clamp keys off this flag to pull it back in range.
export function ioIsWindowedList(io: {
  value: unknown;
  truncated?: boolean;
  list_offset?: number;
}): boolean {
  if (!Array.isArray(io.value)) return false;
  if (io.value.length > 0) return true;
  return io.truncated === true && (io.list_offset ?? 0) > 0;
}
